// ============================================================
// Carrier scorecard — per-carrier split of the demo history in
// statsData.js. Each carrier has its own behaviour so the ranking
// tells a story: D2 does not announce, D1 often arrives out of
// hours, D3/D4 are reliable. Fictional carriers from arrivalsData.
// ============================================================

import { history, rng } from './statsData';
import { carriers } from './arrivalsData';

// Share of daily loadings and behaviour per carrier (before → after pilot).
// noShow is a relative weight for splitting the daily no-shows, not a rate.
const profiles = {
  D1: { share: 0.30, noShow: [0.30, 0.08], outOfHours: [0.10, 0.07], reply: [14, 24], onTime: [0.62, 0.80] },
  D2: { share: 0.25, noShow: [0.45, 0.16], outOfHours: [0.04, 0.03], reply: [35, 60], onTime: [0.50, 0.68] },
  D3: { share: 0.22, noShow: [0.12, 0.06], outOfHours: [0.02, 0.01], reply: [6, 12], onTime: [0.82, 0.93] },
  D4: { share: 0.15, noShow: [0.10, 0.05], outOfHours: [0.01, 0.01], reply: [4, 9], onTime: [0.86, 0.95] },
  D5: { share: 0.08, noShow: [0.25, 0.12], outOfHours: [0.02, 0.02], reply: [20, 45], onTime: [0.65, 0.78] },
};

const pick = (pair, pilot) => (pilot ? pair[1] : pair[0]);

// Split `total` into whole numbers proportional to `weights` (largest remainder).
function split(total, weights) {
  const sum = weights.reduce((s, w) => s + w, 0) || 1;
  const raw = weights.map(w => (total * w) / sum);
  const out = raw.map(Math.floor);
  let left = total - out.reduce((s, x) => s + x, 0);
  raw.map((x, i) => [x - out[i], i]).sort((a, b) => b[0] - a[0]).forEach(([, i]) => {
    if (left > 0) { out[i] += 1; left -= 1; }
  });
  return out;
}

// Daily loadings and no-shows from statsData are split across carriers,
// so carrier totals always add up to the KPIs on the overview tab.
function buildCarrierHistory() {
  const r = rng(4242);
  const ids = Object.keys(profiles);
  return history.map(day => {
    const loadings = split(day.planned, ids.map(id => profiles[id].share * (0.9 + r() * 0.2)));
    const noShowWeights = ids.map((id, i) => loadings[i] * pick(profiles[id].noShow, day.pilot));
    const noShows = split(day.noShows, noShowWeights).map((n, i) => Math.min(n, loadings[i]));
    const perCarrier = {};
    ids.forEach((id, i) => {
      const p = profiles[id];
      perCarrier[id] = {
        loadings: loadings[i],
        noShows: noShows[i],
        outOfHours: r() < pick(p.outOfHours, day.pilot) * loadings[i] ? 1 : 0,
        // Reply time is only measured once the automatic follow-up runs.
        replyMinutes: day.pilot ? Math.round(p.reply[0] + r() * (p.reply[1] - p.reply[0])) : null,
        onTime: Math.round((loadings[i] - noShows[i]) * pick(p.onTime, day.pilot) * (0.9 + r() * 0.2)),
      };
    });
    return { date: day.date, pilot: day.pilot, carriers: perCarrier };
  });
}

export const carrierHistory = buildCarrierHistory();

const median = (xs) => {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  return s[Math.floor(s.length / 2)];
};

export function aggregate(rows, id) {
  const days = rows.map(r => r.carriers[id]);
  const loadings = days.reduce((s, d) => s + d.loadings, 0);
  const noShows = days.reduce((s, d) => s + d.noShows, 0);
  const onTime = days.reduce((s, d) => s + Math.min(d.onTime, d.loadings - d.noShows), 0);
  return {
    loadings,
    announcedPct: loadings ? ((loadings - noShows) / loadings) * 100 : 0,
    noShowPct: loadings ? (noShows / loadings) * 100 : 0,
    outOfHours: days.reduce((s, d) => s + d.outOfHours, 0),
    replyMedian: median(days.map(d => d.replyMinutes).filter(m => m != null)),
    onTimePct: loadings - noShows ? (onTime / (loadings - noShows)) * 100 : 0,
  };
}

// Scorecard for the last `days` working days, with the previous period for the trend.
export function carrierScorecard(days) {
  const current = carrierHistory.slice(-days);
  const previous = carrierHistory.slice(-2 * days, -days);
  return carriers.map(c => ({
    carrier: c,
    now: aggregate(current, c.id),
    prev: previous.length ? aggregate(previous, c.id) : null,
  }));
}

// Weekly series for one carrier (detail chart).
export function carrierWeekly(id) {
  const weeks = [];
  for (let i = 0; i < carrierHistory.length; i += 5) {
    const chunk = carrierHistory.slice(i, i + 5);
    const a = aggregate(chunk, id);
    weeks.push({ date: chunk[0].date, pilot: chunk[0].pilot, noShowPct: Math.round(a.noShowPct), onTimePct: Math.round(a.onTimePct) });
  }
  return weeks;
}
